import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Icon,
  IconButton,
  Switch,
  TextField,
  Typography
} from '@material-ui/core';
import { AddCircleOutline, DeleteOutline, Save } from '@material-ui/icons';
import { SettingsService } from '../../hooks/SettingsService';
import { extractApiErrorMessage, notifications } from '../../utils';
import useAuthContext from '../../hooks/useAuthContext';
import UserRole from '../../constants/user-role';
import { Skeleton } from '../../elements/ui/skeleton';

const PIG_EXPERT_TYPE = 'PigExpert';
const EPIGS_TYPE = 'ePigs';
const WHYSOR_TYPE = 'Whysor';

interface PigExpertSettings {
  databaseId: string;
  compId: string;
  siteId: string;
  exportEnabled: boolean;
}

interface EPigsSettings {
  ubns: string[];
}

interface WhysorSettings {
  organizationName: string;
}

const EMPTY_PIG_EXPERT_SETTINGS: PigExpertSettings = { databaseId: '', compId: '', siteId: '', exportEnabled: false };
const EMPTY_EPIGS_SETTINGS: EPigsSettings = { ubns: [''] };
const EMPTY_WHYSOR_SETTINGS: WhysorSettings = { organizationName: '' };

const ExternalProviders: React.FC = () => {
  const { t } = useTranslation();
  const { state } = useAuthContext();
  const settingsService = new SettingsService();

  /**
   * The switched-to business unit when there is one, and otherwise the farm the token itself names.
   *
   * `selectedBusinessUnitId` alone was never populated on an ordinary farm login: it comes from a `unitId`
   * claim only the `/admin` business-unit flow mints, so it was `''` for everyone arriving through
   * `/auth/webuser`. `loadProviders` returns early on a falsy farmId, so this panel sat on its skeleton
   * forever and issued no request at all - the Whysor configuration the Sensor devices tab sends people
   * here to fix was unreachable. Every other farm-scoped screen reads `state.farmId`.
   */
  const farmId = state.selectedBusinessUnitId || state.farmId;

  const [, setIsBusy] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isTogglingEnabled, setIsTogglingEnabled] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);

  const [pigExpertEnabled, setPigExpertEnabled] = React.useState(false);
  const [pigExpertExists, setPigExpertExists] = React.useState(false);
  const [settings, setSettings] = React.useState<PigExpertSettings>(EMPTY_PIG_EXPERT_SETTINGS);

  const [ePigsEnabled, setEPigsEnabled] = React.useState(false);
  const [ePigsExists, setEPigsExists] = React.useState(false);
  const [ePigsSettings, setEPigsSettings] = React.useState<EPigsSettings>(EMPTY_EPIGS_SETTINGS);
  const [ePigsUbnErrors, setEPigsUbnErrors] = React.useState<(string | null)[]>([]);
  const [isTogglingEPigs, setIsTogglingEPigs] = React.useState(false);
  const [isSavingEPigs, setIsSavingEPigs] = React.useState(false);

  const [whysorEnabled, setWhysorEnabled] = React.useState(false);
  const [whysorExists, setWhysorExists] = React.useState(false);
  const [whysorSettings, setWhysorSettings] = React.useState<WhysorSettings>(EMPTY_WHYSOR_SETTINGS);
  const [isTogglingWhysor, setIsTogglingWhysor] = React.useState(false);
  const [isSavingWhysor, setIsSavingWhysor] = React.useState(false);

  const loadProviders = React.useCallback(async () => {
    if (!farmId) return;
    setIsBusy(true);
    settingsService
      .getConfigurationProviders(farmId)
      .then((providers) => {
        const provider = providers.find((p) => p.providerType === PIG_EXPERT_TYPE);
        if (provider) {
          setPigExpertExists(true);
          setPigExpertEnabled(provider.isEnabled);
          const s = provider.settings ?? {};
          setSettings({
            databaseId: s.databaseId != null ? String(s.databaseId) : '',
            compId: s.compId != null ? String(s.compId) : '',
            siteId: s.siteId != null ? String(s.siteId) : '',
            exportEnabled: s.exportEnabled === true
          });
        }

        const ePigsProvider = providers.find((p) => p.providerType === EPIGS_TYPE);
        if (ePigsProvider) {
          setEPigsExists(true);
          setEPigsEnabled(ePigsProvider.isEnabled);
          const s = ePigsProvider.settings ?? {};
          const ubns = Array.isArray(s.ubns) ? s.ubns.map((ubn: unknown) => String(ubn)) : [];
          setEPigsSettings({ ubns: ubns.length > 0 ? ubns : [''] });
          setEPigsUbnErrors([]);
        }

        const whysorProvider = providers.find((p) => p.providerType === WHYSOR_TYPE);
        if (whysorProvider) {
          setWhysorExists(true);
          setWhysorEnabled(whysorProvider.isEnabled);
          const s = whysorProvider.settings ?? {};
          setWhysorSettings({ organizationName: s.organizationName != null ? String(s.organizationName) : '' });
        }

        setLoaded(true);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  }, [farmId, settingsService, t]);

  React.useEffect(() => {
    if (!loaded) {
      loadProviders();
    }
  }, [loaded, loadProviders]);

  const handleToggleEnabled = async () => {
    if (!farmId || isTogglingEnabled) return;
    setIsTogglingEnabled(true);
    const next = !pigExpertEnabled;
    try {
      if (next) {
        await settingsService.enableConfigurationProvider(farmId, PIG_EXPERT_TYPE);
        notifications.success(t('f-settings-ext-enable-ok'));
      } else {
        await settingsService.disableConfigurationProvider(farmId, PIG_EXPERT_TYPE);
        notifications.success(t('f-settings-ext-disable-ok'));
      }
      setPigExpertEnabled(next);
    } catch (e) {
      notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
    } finally {
      setIsTogglingEnabled(false);
    }
  };

  const handleSave = async () => {
    if (!farmId || isSaving) return;
    setIsSaving(true);
    const payload = {
      databaseId: settings.databaseId !== '' ? Number(settings.databaseId) : null,
      compId: settings.compId !== '' ? Number(settings.compId) : null,
      siteId: settings.siteId !== '' ? Number(settings.siteId) : null,
      exportEnabled: settings.exportEnabled
    };
    settingsService
      .upsertConfigurationProvider(farmId, PIG_EXPERT_TYPE, payload)
      .then(() => {
        notifications.success(t('f-settings-ext-save-ok'));
        loadProviders();
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const handleToggleEPigs = async () => {
    if (!farmId || isTogglingEPigs) return;
    setIsTogglingEPigs(true);
    const next = !ePigsEnabled;
    try {
      if (next) {
        await settingsService.enableConfigurationProvider(farmId, EPIGS_TYPE);
        notifications.success(t('f-settings-ext-enable-ok'));
      } else {
        await settingsService.disableConfigurationProvider(farmId, EPIGS_TYPE);
        notifications.success(t('f-settings-ext-disable-ok'));
      }
      setEPigsEnabled(next);
    } catch (e) {
      notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
    } finally {
      setIsTogglingEPigs(false);
    }
  };

  const handleAddEPigsUbn = () => {
    setEPigsSettings((prev) => ({ ubns: [...prev.ubns, ''] }));
    setEPigsUbnErrors((prev) => [...prev, null]);
  };

  const handleRemoveEPigsUbn = (index: number) => {
    setEPigsSettings((prev) => ({ ubns: prev.ubns.filter((_, i) => i !== index) }));
    setEPigsUbnErrors((prev) => prev.filter((_, i) => i !== index));
  };

  const handleChangeEPigsUbn = (index: number, value: string) => {
    setEPigsSettings((prev) => ({ ubns: prev.ubns.map((ubn, i) => (i === index ? value : ubn)) }));
    setEPigsUbnErrors((prev) => prev.map((err, i) => (i === index ? null : err)));
  };

  const handleSaveEPigs = async () => {
    if (!farmId || isSavingEPigs) return;

    const trimmedUbns = ePigsSettings.ubns.map((ubn) => ubn.trim());
    const errors = trimmedUbns.map((ubn) =>
      ubn.length > 0 && ubn.length !== 7 ? t('f-settings-ext-epigs-ubn-error') : null
    );
    if (errors.some((error) => error !== null)) {
      setEPigsUbnErrors(errors);
      return;
    }

    const ubns = trimmedUbns.filter((ubn) => ubn.length > 0);
    if (ubns.length === 0) {
      notifications.error(t('f-settings-ext-epigs-ubn-required'));
      return;
    }

    if (new Set(ubns).size !== ubns.length) {
      notifications.error(t('f-settings-ext-epigs-ubn-duplicate'));
      return;
    }

    setEPigsUbnErrors([]);
    setIsSavingEPigs(true);
    settingsService
      .upsertConfigurationProvider(farmId, EPIGS_TYPE, { ubns })
      .then(() => {
        notifications.success(t('f-settings-ext-save-ok'));
        loadProviders();
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsSavingEPigs(false);
      });
  };

  const handleToggleWhysor = async () => {
    if (!farmId || isTogglingWhysor) return;
    setIsTogglingWhysor(true);
    const next = !whysorEnabled;
    try {
      if (next) {
        await settingsService.enableConfigurationProvider(farmId, WHYSOR_TYPE);
        notifications.success(t('f-settings-ext-enable-ok'));
      } else {
        await settingsService.disableConfigurationProvider(farmId, WHYSOR_TYPE);
        notifications.success(t('f-settings-ext-disable-ok'));
      }
      setWhysorEnabled(next);
    } catch (e) {
      notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
    } finally {
      setIsTogglingWhysor(false);
    }
  };

  const handleSaveWhysor = async () => {
    if (!farmId || isSavingWhysor) return;
    setIsSavingWhysor(true);
    settingsService
      .upsertConfigurationProvider(farmId, WHYSOR_TYPE, { organizationName: whysorSettings.organizationName })
      .then(() => {
        notifications.success(t('f-settings-ext-save-ok'));
        loadProviders();
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsSavingWhysor(false);
      });
  };

  const isReadOnly = state.role === UserRole.user;

  return (
    <>
      {!loaded ? (
        <Card elevation={1}>
          <CardContent>
            <div className="flex items-center gap-2 mb-4">
              <Skeleton className="h-8 w-8 rounded-full" />
              <Skeleton className="h-6 w-48" />
            </div>
            <Skeleton className="h-px w-full mb-4" />
            {[0, 1, 2].map((i) => (
              <div key={i} className="rounded-lg border border-gray-200 p-4 mb-4 last:mb-0">
                <div className="flex justify-between items-start mb-4">
                  <div className="space-y-2">
                    <Skeleton className="h-5 w-40" />
                    <Skeleton className="h-4 w-72" />
                  </div>
                  <Skeleton className="h-5 w-16 rounded-full" />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <Skeleton className="h-9 w-full rounded" />
                  <Skeleton className="h-9 w-full rounded" />
                  <Skeleton className="h-9 w-full rounded" />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : (
        <Card elevation={1}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" className="gap-2">
                  <Icon color="primary" fontSize="large">
                    cloud_sync
                  </Icon>
                  <Typography color="primary" variant="h6" style={{ fontWeight: 600 }}>
                    {t('f-settings-ext-providers')}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* AgroVision PigExpert */}
              <Grid item xs={12}>
                <Box className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                  <Grid container spacing={2}>
                    <Grid item container xs={12} justifyContent="space-between" alignItems="center">
                      <Grid item>
                        <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
                          {t('f-settings-ext-pigexpert')}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {t('f-settings-ext-pigexpert-desc')}
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Box display="flex" alignItems="center" className="gap-2">
                          <Typography variant="body2" color="textSecondary">
                            {t('f-settings-ext-enabled')}
                          </Typography>
                          <Switch
                            checked={pigExpertEnabled}
                            onChange={handleToggleEnabled}
                            disabled={isReadOnly || isTogglingEnabled || !pigExpertExists}
                            color="primary"
                            size="small"
                          />
                        </Box>
                      </Grid>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        label={t('f-settings-ext-pigexpert-db-id')}
                        value={settings.databaseId}
                        onChange={(e) => setSettings({ ...settings, databaseId: e.target.value })}
                        variant="outlined"
                        size="small"
                        fullWidth
                        type="number"
                        disabled={isReadOnly || isSaving}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        label={t('f-settings-ext-pigexpert-comp-id')}
                        value={settings.compId}
                        onChange={(e) => setSettings({ ...settings, compId: e.target.value })}
                        variant="outlined"
                        size="small"
                        fullWidth
                        type="number"
                        disabled={isReadOnly || isSaving}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        label={t('f-settings-ext-pigexpert-site-id')}
                        value={settings.siteId}
                        onChange={(e) => setSettings({ ...settings, siteId: e.target.value })}
                        variant="outlined"
                        size="small"
                        fullWidth
                        type="number"
                        disabled={isReadOnly || isSaving}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <div>
                          <Typography variant="body2" style={{ fontWeight: 500 }}>
                            {t('f-settings-ext-pigexpert-export')}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {t('f-settings-ext-pigexpert-export-desc')}
                          </Typography>
                        </div>
                        <Switch
                          checked={settings.exportEnabled}
                          onChange={(e) => setSettings({ ...settings, exportEnabled: e.target.checked })}
                          disabled={isReadOnly || isSaving}
                          color="primary"
                          size="small"
                        />
                      </Box>
                    </Grid>

                    {!isReadOnly && (
                      <Grid item xs={12}>
                        <Box display="flex" justifyContent="flex-end">
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={<Save />}
                            onClick={handleSave}
                            disabled={isSaving}
                          >
                            {t('f-com-save')}
                          </Button>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Grid>

              {/* ePigs (Compaxo) */}
              <Grid item xs={12}>
                <Box className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                  <Grid container spacing={2}>
                    <Grid item container xs={12} justifyContent="space-between" alignItems="center">
                      <Grid item>
                        <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
                          {t('f-settings-ext-epigs')}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {t('f-settings-ext-epigs-desc')}
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Box display="flex" alignItems="center" className="gap-2">
                          <Typography variant="body2" color="textSecondary">
                            {t('f-settings-ext-enabled')}
                          </Typography>
                          <Switch
                            checked={ePigsEnabled}
                            onChange={handleToggleEPigs}
                            disabled={isReadOnly || isTogglingEPigs || !ePigsExists}
                            color="primary"
                            size="small"
                          />
                        </Box>
                      </Grid>
                    </Grid>

                    {ePigsSettings.ubns.map((ubn, index) => (
                      <Grid item xs={12} sm={4} key={index}>
                        <Box display="flex" alignItems="flex-start" className="gap-1">
                          <TextField
                            label={t('f-settings-ext-epigs-ubn')}
                            value={ubn}
                            onChange={(e) => handleChangeEPigsUbn(index, e.target.value)}
                            variant="outlined"
                            size="small"
                            fullWidth
                            disabled={isReadOnly || isSavingEPigs}
                            InputLabelProps={{ shrink: true }}
                            error={!!ePigsUbnErrors[index]}
                            helperText={ePigsUbnErrors[index] ?? undefined}
                            inputProps={{ maxLength: 7 }}
                          />
                          {!isReadOnly && ePigsSettings.ubns.length > 1 && (
                            <IconButton
                              size="small"
                              aria-label={t('f-settings-ext-epigs-ubn-remove')}
                              onClick={() => handleRemoveEPigsUbn(index)}
                              disabled={isSavingEPigs}
                            >
                              <DeleteOutline color="error" fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </Grid>
                    ))}

                    {!isReadOnly && (
                      <Grid item xs={12}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Button
                            variant="text"
                            color="primary"
                            size="small"
                            startIcon={<AddCircleOutline />}
                            onClick={handleAddEPigsUbn}
                            disabled={isSavingEPigs}
                          >
                            {t('f-settings-ext-epigs-ubn-add')}
                          </Button>
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={<Save />}
                            onClick={handleSaveEPigs}
                            disabled={isSavingEPigs}
                          >
                            {t('f-com-save')}
                          </Button>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Grid>

              {/* Whysor */}
              <Grid item xs={12}>
                <Box className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                  <Grid container spacing={2}>
                    <Grid item container xs={12} justifyContent="space-between" alignItems="center">
                      <Grid item>
                        <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
                          {t('f-settings-ext-whysor')}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {t('f-settings-ext-whysor-desc')}
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Box display="flex" alignItems="center" className="gap-2">
                          <Typography variant="body2" color="textSecondary">
                            {t('f-settings-ext-enabled')}
                          </Typography>
                          <Switch
                            checked={whysorEnabled}
                            onChange={handleToggleWhysor}
                            disabled={isReadOnly || isTogglingWhysor || !whysorExists}
                            color="primary"
                            size="small"
                          />
                        </Box>
                      </Grid>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        label={t('f-settings-ext-whysor-org-name')}
                        value={whysorSettings.organizationName}
                        onChange={(e) => setWhysorSettings({ organizationName: e.target.value })}
                        variant="outlined"
                        size="small"
                        fullWidth
                        disabled={isReadOnly || isSavingWhysor}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    {!isReadOnly && (
                      <Grid item xs={12}>
                        <Box display="flex" justifyContent="flex-end">
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={<Save />}
                            onClick={handleSaveWhysor}
                            disabled={isSavingWhysor}
                          >
                            {t('f-com-save')}
                          </Button>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default ExternalProviders;
